import type { ResignationFormContext } from "./types";

function asTrimmedId(v: unknown): string | undefined {
  if (typeof v === "string" && v.trim()) return v.trim();
  return undefined;
}

/** Read id from a small object (nested DTOs from GET /v2/exit/resignation-form). */
function idFromObjectLoose(
  o: Record<string, unknown> | null | undefined,
  keys: string[],
): string | undefined {
  if (!o) return undefined;
  for (const k of keys) {
    const id = asTrimmedId(o[k]);
    if (id) return id;
  }
  return undefined;
}

const NESTED_CONTAINERS = [
  "activeExit",
  "openExit",
  "blockingExit",
  "inFlightExit",
  "activeTermination",
  "blockingTermination",
  "terminationContext",
] as const;

const NESTED_ID_KEYS = [
  "id",
  "exitId",
  "exit_id",
  "linkedExitId",
  "linked_exit_id",
  "inFlightExitId",
  "in_flight_exit_id",
  "openExitId",
  "open_exit_id",
  "activeExitId",
  "active_exit_id",
] as const;

/**
 * Some backends expose the employee’s in-flight exit id on the resignation-form
 * payload so clients with only RESIGNATION_* (no GET /v2/exit/my-exit) can still open
 * checklist / lifecycle. Accepts camelCase or snake_case keys, and common nested
 * shapes (e.g. termination + linked exit id).
 */
export function pickActiveExitIdFromResignationForm(
  formCtx: ResignationFormContext | undefined,
): string | undefined {
  if (!formCtx) return undefined;
  const r = formCtx as Record<string, unknown>;

  const directCandidates: unknown[] = [
    r.activeExitId,
    r.currentExitId,
    r.primaryExitId,
    r.inProgressExitId,
    r.active_exit_id,
    r.current_exit_id,
    r.linkedExitId,
    r.linked_exit_id,
    r.inFlightExitId,
    r.in_flight_exit_id,
    r.openExitId,
    r.open_exit_id,
    r.currentInFlightExitId,
    r.current_in_flight_exit_id,
    r.employeeExitId,
    r.employee_exit_id,
    r.activeLinkedExitId,
    r.active_linked_exit_id,
    r.blockingExitId,
    r.blocking_exit_id,
  ];
  for (const c of directCandidates) {
    const id = asTrimmedId(c);
    if (id) return id;
  }

  for (const container of NESTED_CONTAINERS) {
    const nested = r[container];
    if (nested && typeof nested === "object" && nested !== null) {
      const id = idFromObjectLoose(
        nested as Record<string, unknown>,
        [...NESTED_ID_KEYS],
      );
      if (id) return id;
    }
  }

  return undefined;
}

/**
 * Dev-only: list top-level string keys and which nested containers exist, to align
 * with the real GET /v2/exit/resignation-form JSON when pickActiveExitIdFromResignationForm is empty.
 */
export function debugResignationFormExitIdShape(
  formCtx: ResignationFormContext | undefined,
): {
  topLevelStringKeys: string[];
  nestedContainersPresent: string[];
} {
  if (!formCtx) {
    return { topLevelStringKeys: [], nestedContainersPresent: [] };
  }
  const r = formCtx as Record<string, unknown>;
  const topLevelStringKeys = Object.keys(r).filter(
    (k) => typeof r[k] === "string" && asTrimmedId(r[k]),
  );
  const nestedContainersPresent = NESTED_CONTAINERS.filter(
    (k) =>
      r[k] != null && typeof r[k] === "object" && r[k] !== null,
  );
  return { topLevelStringKeys, nestedContainersPresent };
}
